import { useCallback, useMemo, useRef } from 'react';
import { AbsoluteFill, Img, useCurrentFrame, useVideoConfig } from 'remotion';
import { SelectionElementType } from '../../Outlines/types';
import { constrainElementPosition } from './utils';
import { useMediaBounds, useElementPositions } from '../hooks/useMediaBounds';
import { SelectionOutline } from '../../Outlines/SelectionOutline';
import { useTransitionStyles } from '../hooks/useTransitionStyles';
import { ImageElement } from '@/App/remotion/MainPlayer/types/Element/ImageElement';
import { getAnimationStylesForFrame } from '@/App/remotion/MainPlayer/utils/animation/getAnimationStylesForFrame';
import { CompositionConfig, ElementPositions } from '@/App/remotion/MainPlayer/types/CompositionConfig';
import { BoxShadowProperties } from '@/App/remotion/MainPlayer/types/Style/BoxShadow';

const ImageElementComponent: React.FC<{
  element: ImageElement;
  onOutlineRelease: CompositionConfig['onOutlineRelease'];
  canEditImages: boolean;
}> = ({ element, onOutlineRelease, canEditImages }) => {
  const { details, style, containerStyle, effects } = element;
  const currentFrame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const imageElementRef = useRef<HTMLDivElement>(null);

  const transitionStyles = useTransitionStyles({ element });

  const { style: animationStyle } = getAnimationStylesForFrame(currentFrame, effects || [], fps);

  const getImageBounds = useMediaBounds(imageElementRef, 'img');

  const isImageStyleContain = useMemo(() => style.objectFit === 'contain', [style.objectFit]);

  const { positions, updatePositions, setPositions } = useElementPositions(
    containerStyle,
    isImageStyleContain,
    getImageBounds
  );

  const transformedElementStyle = useMemo(
    () => ({
      ...style,
      boxShadow: (style.boxShadow || [])
        .map(
          (shadow: BoxShadowProperties[0]) =>
            `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.spread}px ${shadow.color}`
        )
        .join(','),
      ...transitionStyles
    }),
    [style, transitionStyles]
  );

  const transformedContainerStyle = useMemo(
    () => ({
      ...containerStyle,
      ...animationStyle,
      ...positions,
      boxShadow: (containerStyle.boxShadow || [])
        .map(
          (shadow: BoxShadowProperties[0]) =>
            `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.spread}px ${shadow.color}`
        )
        .join(',')
    }),
    [containerStyle, animationStyle, positions]
  );

  const handleImageLoad = useCallback(() => {
    if (!isImageStyleContain || !canEditImages) {
      return;
    }

    const imageBounds = getImageBounds();
    if (imageBounds) {
      setPositions({
        top: `${imageBounds.top}%`,
        left: `${imageBounds.left}%`,
        width: `${imageBounds.width}%`,
        height: `${imageBounds.height}%`
      });
    }
  }, [isImageStyleContain, getImageBounds, setPositions, canEditImages]);

  // Handle selection outline release
  const onSelectionOutlineRelease = useCallback(
    (newPositions: ElementPositions) => {
      const parentElement = imageElementRef.current?.parentElement;
      const parentDimensions = parentElement ? parentElement.getBoundingClientRect() : null;

      const constrainedPositions = constrainElementPosition(newPositions, parentDimensions);

      const isPositionsSame =
        constrainedPositions.top === positions.top &&
        constrainedPositions.left === positions.left &&
        constrainedPositions.width === positions.width &&
        constrainedPositions.height === positions.height;

      if (isPositionsSame) {
        return;
      }

      setPositions(constrainedPositions);
      onOutlineRelease?.(constrainedPositions, element);
    },
    [onOutlineRelease, positions, element, setPositions]
  );

  return (
    <AbsoluteFill>
      <div ref={imageElementRef} style={transformedContainerStyle}>
        <Img
          style={transformedElementStyle}
          src={details.src}
          alt={details.altText}
          pauseWhenLoading
          onLoad={handleImageLoad}
        />
      </div>
      {canEditImages && (
        <SelectionOutline
          positions={positions}
          updatePositions={updatePositions}
          onOutlineRelease={onSelectionOutlineRelease}
          allowResize={true}
          elementType={SelectionElementType.IMAGE}
          data={{ elementId: element.id, type: SelectionElementType.IMAGE }}
          maintainAspectRatio={isImageStyleContain}
        />
      )}
    </AbsoluteFill>
  );
};

export default ImageElementComponent;
